import { BREAKPOINTS, EVENTS, QUICK_BUY, SHOP_THE_LOOK } from '../../utils/constants';
import safeJSONParse from '../../utils/safeJsonParse';
import fetchProductView from '../../lib/fetchProductView';
import initProductVariants from '../../components/product-variants';
import initAtcForm from '../../components/atcForm';
import initQuantityInputs from '../../components/quantityInputs';
import { initSwiper, destroySwipers } from '../../components/swiper';

// PDP "Complete Your Kitchen" (sections/product-shop-the-look.liquid). On
// desktop a hotspot or thumb loads that product into the inline panel; on
// mobile the panel is hidden and hotspots fall through to the quick-buy
// drawer's document-level listener.

const SECTION_SELECTOR = '[data-shop-the-look]';
const BUSY_ATTR = 'aria-busy';
const PRESSED_ATTR = 'aria-pressed';

// The panel runs the variant picker in popup mode: its own price container,
// and no URL or PDP message updates for a product that is not the page's
const initPanelForms = (panelEl) => {
  initProductVariants(panelEl, { isPopup: true });
  initQuantityInputs(panelEl);
  initAtcForm(false, panelEl);
};

// Swapped-in markup also needs its gallery started (the server-rendered
// first product's swiper is picked up by the global init)
const initPanel = (panelEl) => {
  panelEl.querySelectorAll('[data-swiper]').forEach((swiperEl) => initSwiper(swiperEl));
  initPanelForms(panelEl);
};

// Screen readers get nothing from the swapped markup itself; the section
// carries the translated "[title]" template for the status line
const announceLoaded = (sectionEl, statusEl, productEl) => {
  if (!statusEl) return;
  const title = productEl.querySelector('[data-product-title]')?.textContent.trim() ?? '';
  statusEl.textContent = (sectionEl.dataset.lookLoadedText ?? '').replace('[title]', title);
};

const setActiveThumb = (sectionEl, handle) => {
  sectionEl.querySelectorAll('[data-look-product]').forEach((thumbEl) => {
    thumbEl.setAttribute(PRESSED_ATTR, String(thumbEl.dataset.lookProduct === handle));
  });
};

const initSection = (sectionEl) => {
  const panelEl = sectionEl.querySelector('[data-look-panel]');
  if (!panelEl) return;

  const statusEl = sectionEl.querySelector('[data-look-status]');
  const errorEl = sectionEl.querySelector('[data-look-error]');
  const desktopMediaQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.DESKTOP}px)`);
  let isLoading = false;

  const showError = (isVisible) => {
    if (errorEl) errorEl.hidden = !isVisible;
  };

  initPanelForms(panelEl);

  const loadProduct = async (handle, triggerEl) => {
    if (isLoading) return;
    isLoading = true;
    triggerEl.setAttribute(BUSY_ATTR, 'true');
    panelEl.setAttribute(BUSY_ATTR, 'true');
    try {
      const viewDoc = await fetchProductView(handle, SHOP_THE_LOOK.VIEW);
      const productEl = viewDoc.querySelector('[data-atc-popup-product]');
      if (!productEl) throw new Error('Shop the look view has no product markup');
      destroySwipers(panelEl);
      panelEl.replaceChildren(productEl);
      initPanel(panelEl);
      setActiveThumb(sectionEl, handle);
      showError(false);
      announceLoaded(sectionEl, statusEl, productEl);
    } catch (error) {
      // The panel keeps the previous product and its pressed thumb, so the
      // selection and the panel still agree; the alert says the click failed
      console.error('Complete Your Kitchen could not load the product:', error);
      showError(true);
    } finally {
      isLoading = false;
      triggerEl.removeAttribute(BUSY_ATTR);
      panelEl.removeAttribute(BUSY_ATTR);
    }
  };

  sectionEl.addEventListener('click', (event) => {
    const triggerEl = event.target.closest(`[data-look-product], ${QUICK_BUY.TRIGGER_SELECTOR}`);
    if (!triggerEl || !desktopMediaQuery.matches) return;
    // Desktop owns the hotspot here: stop the click before the document-level
    // quick-buy listener opens the drawer
    event.stopPropagation();
    loadProduct(triggerEl.dataset.lookProduct || triggerEl.dataset.quickBuy, triggerEl);
  });
};

const bindSection = (sectionEl) => {
  if (sectionEl.dataset.shopTheLookInit) return;
  sectionEl.dataset.shopTheLookInit = 'true';
  initSection(sectionEl);
};

// A look change re-renders the section server-side with the new variant
// selected (Section Rendering API), so the image, hotspots, thumbs and panel
// all come from the same Liquid as first paint
const fetchSection = async (sectionEl, variantId) => {
  const { sectionId } = sectionEl.dataset;
  const url = `${location.pathname}?variant=${variantId}&sections=${sectionId}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
  const sections = await response.json();
  const sectionDoc = new DOMParser().parseFromString(sections[sectionId] ?? '', 'text/html');
  const nextSectionEl = sectionDoc.querySelector(SECTION_SELECTOR);
  if (!nextSectionEl) throw new Error('Section render has no shop the look markup');
  return nextSectionEl;
};

let latestSwapId = 0;

const swapSection = async (sectionEl, variantId) => {
  latestSwapId += 1;
  const swapId = latestSwapId;
  sectionEl.setAttribute(BUSY_ATTR, 'true');
  try {
    const nextSectionEl = await fetchSection(sectionEl, variantId);
    // A later variant change already has its own request in flight
    if (swapId !== latestSwapId) return;
    destroySwipers(sectionEl);
    sectionEl.replaceWith(nextSectionEl);
    nextSectionEl.querySelectorAll('[data-swiper]').forEach((swiperEl) => initSwiper(swiperEl));
    bindSection(nextSectionEl);
  } catch (error) {
    console.error('Complete Your Kitchen could not update for the variant:', error);
  } finally {
    sectionEl.removeAttribute(BUSY_ATTR);
  }
};

// Only a change of look costs a request: sizes of one colour usually share
// a look, and a variant with none just hides the section
const onVariantChanged = (event) => {
  const variantId = String(event.detail?.variantId ?? '');
  document.querySelectorAll(SECTION_SELECTOR).forEach((sectionEl) => {
    const lookMap = safeJSONParse(sectionEl.dataset.lookMap, {}) || {};
    const nextLookId = lookMap[variantId] ?? '';
    if (nextLookId === (sectionEl.dataset.lookId ?? '')) return;
    if (!nextLookId) {
      // Also retires any swap still in flight, so a slower fetch for the
      // previous variant can't land on a section that should now be hidden
      latestSwapId += 1;
      sectionEl.hidden = true;
      sectionEl.dataset.lookId = '';
      return;
    }
    swapSection(sectionEl, variantId);
  });
};

let isVariantListenerBound = false;

const initShopTheLook = () => {
  document.querySelectorAll(SECTION_SELECTOR).forEach(bindSection);
  if (isVariantListenerBound) return;
  isVariantListenerBound = true;
  document.addEventListener(EVENTS.VARIANT_CHANGED, onVariantChanged);
};

export default initShopTheLook;
